// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Avatar, AvatarProps, styled } from '@mui/material';
import { ChangeEventHandler, PropsWithChildren, useRef } from 'react';

const FileInput = styled('input')({
  display: 'none',
});

const ImageAvatar = styled(Avatar)((props) => ({
  cursor: 'pointer',
  borderRadius: props.theme.shape.borderRadius,
  backgroundColor: props.src === undefined ? undefined : 'transparent',
  '& > img': {
    objectFit: 'contain',
  },
}));

interface Props extends Pick<AvatarProps, 'sx'> {
  /**
   * The source of the avatar image.
   */
  source: string | undefined;

  /**
   * A callback fired when a new file is selected.
   */
  onChange: (file: File) => void;
}

/**
 * An input component that renders a clickable avatar image and allows the user
 * to select a file.
 */
export function AvatarInput(props: PropsWithChildren<Props>) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.target.files === null) {
      return;
    }

    props.onChange(event.target.files[0]);
  };

  return (
    <>
      <FileInput
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleChange}
      />
      <ImageAvatar sx={props.sx} src={props.source} onClick={handleClick}>
        {props.children}
      </ImageAvatar>
    </>
  );
}
